import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { loadIdentity } from "../lib/identity";

function VoiceMaskIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 8.5c2.4-1.8 5-2.7 8-2.7s5.6.9 8 2.7l-1.1 6.2c-.3 1.8-1.8 3.1-3.6 3.1-1.4 0-2.6-.7-3.3-1.9-.7 1.2-1.9 1.9-3.3 1.9-1.8 0-3.3-1.3-3.6-3.1L4 8.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M7.5 11.2c.8-.5 1.7-.5 2.5 0M14 11.2c.8-.5 1.7-.5 2.5 0M12 15.9v2.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

export const VOICE_PRESETS = [
  {
    key: "off",
    label: "Disattivato",
    hint: "Voce originale",
    minRank: 0,
  },
  {
    key: "acuto",
    label: "Acuto",
    hint: "Pitch alzato dopo la registrazione (~+4 semitoni)",
    minRank: 0,
  },
  {
    key: "grave",
    label: "Basso",
    hint: "Pitch abbassato dopo la registrazione (~-4 semitoni)",
    minRank: 0,
  },

  {
    key: "brillante",
    label: "Brillante",
    hint: "Voce leggermente più alta e brillante (~+2 semitoni)",
    minRank: 2,
  },
  {
    key: "caldo",
    label: "Caldo",
    hint: "Voce leggermente più bassa e calda (~-2 semitoni)",
    minRank: 2,
  },
  {
    key: "acuto_intenso",
    label: "Acuto intenso",
    hint: "Alterazione più marcata verso l'alto (~+6 semitoni)",
    minRank: 2,
  },
  {
    key: "profondo",
    label: "Profondo",
    hint: "Alterazione più marcata verso il basso (~-6 semitoni)",
    minRank: 2,
  },

  {
    key: "creator_high",
    label: "Creator Alto",
    hint: "Preset Creator Pro ad alterazione elevata (~+8 semitoni)",
    minRank: 3,
  },
  {
    key: "creator_deep",
    label: "Creator Profondo",
    hint: "Preset Creator Pro ad alterazione profonda (~-8 semitoni)",
    minRank: 3,
  },
];

export function voicePresetLabel(value) {
  return VOICE_PRESETS.find((preset) => preset.key === value)?.label ?? "Disattivato";
}

export function voicePresetHintText(preset) {
  return (
    VOICE_PRESETS.find((item) => item.key === preset)?.hint ||
    "Voce originale"
  );
}

function VoicePresetModal({ value, onChange, onClose, testId }) {
  const [voicePremiumRank, setVoicePremiumRank] = React.useState(0);
  const [voiceEntitlementLoading, setVoiceEntitlementLoading] =
    React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    const loadVoiceEntitlement = async () => {
      try {
        const identity = loadIdentity();
        const identityId = identity?.identityId;

        if (!identityId) {
          if (!cancelled) {
            setVoicePremiumRank(0);
          }
          return;
        }

        const response = await fetch(
          "/api/v1/payments/entitlement",
          {
            method: "GET",
            credentials: "include",
            headers: {
              "x-ol-identity": identityId,
            },
          }
        );

        const payload = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.message ||
            payload?.error ||
            "Impossibile leggere il piano Premium."
          );
        }

        const rankByPlan = {
          premium_start: 1,
          premium_plus: 2,
          creator_pro: 3,
        };

        const rank = payload?.active
          ? rankByPlan[payload?.plan] || 0
          : 0;

        if (!cancelled) {
          setVoicePremiumRank(rank);
        }
      } catch (error) {
        console.error(
          "VoicePresetPicker: entitlement failed",
          error
        );

        if (!cancelled) {
          // fail-safe: se non sappiamo il piano,
          // mostriamo soltanto i preset Free
          setVoicePremiumRank(0);
        }
      } finally {
        if (!cancelled) {
          setVoiceEntitlementLoading(false);
        }
      }
    };

    void loadVoiceEntitlement();

    return () => {
      cancelled = true;
    };
  }, []);

  const allowedVoicePresets = VOICE_PRESETS.filter(
    (preset) =>
      Number(preset.minRank || 0) <= voicePremiumRank
  );

  React.useEffect(() => {
    if (voiceEntitlementLoading) return;

    const allowed = allowedVoicePresets.some(
      (preset) => preset.key === value
    );

    if (!allowed && value !== "off") {
      onChange("off");
    }
  }, [
    voicePremiumRank,
    voiceEntitlementLoading,
    value,
    onChange,
  ]);
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const keydown = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", keydown);
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-ink/55 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Chiudi modificatore di voce"
      />
      <section
        className="relative z-[1001] w-[min(92vw,360px)] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl border-2 border-ink bg-cream p-4 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Modificatore di voce"
        data-testid={testId + "-dialog"}
      >
        <header className="sticky top-0 -mx-1 -mt-1 mb-3 flex items-center justify-between rounded-2xl bg-cream px-2 py-1">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">Opinione-Libera</p>
            <h2 className="text-xl font-bold text-ink">Modificatore di voce</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-edge bg-white text-ink"
            aria-label="Chiudi"
            data-testid={testId + "-close"}
          >
            <X size={20} />
          </button>
        </header>
        <div className="space-y-2" role="radiogroup" aria-label="Scegli il modificatore di voce">
          {allowedVoicePresets.map((preset) => {
            const active = value === preset.key;
            return (
              <button
                key={preset.key}
                type="button"
                role="radio"
                aria-checked={active}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition ${active ? "border-primary bg-primary text-white" : "border-edge bg-white text-ink"}`}
                onClick={() => { onChange(preset.key); onClose(); }}
                data-testid={testId + "-" + preset.key}
              >
                <span className="block text-base font-bold">{preset.label}</span>
                <small className={active ? "text-white/85" : "text-muted"}>{preset.hint}</small>
              </button>
            );
          })}
        </div>
      </section>
    </div>,
    document.body,
  );
}

export function VoicePresetPicker({
  value,
  disabled = false,
  onChange,
  testId = "voice-preset",
  compact = false,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedPreset = VOICE_PRESETS.find((preset) => preset.key === value) ?? VOICE_PRESETS[0];
  const close = React.useCallback(() => setOpen(false), []);

  if (compact) {
    return (
      <>
        <button
          type="button"
          className="voice-preset-compact !w-10 !h-10 !min-w-10 !p-0 !gap-0 !justify-center"
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Modificatore di voce. Stato: ${selectedPreset.label}`}
          data-testid={testId}
        >
          <VoiceMaskIcon size={20} />
          <span className="sr-only">Stato: {selectedPreset.label}</span>
        </button>
        {open && (
          <VoicePresetModal
            value={value}
            onChange={onChange}
            onClose={close}
            testId={testId}
          />
        )}
      </>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-0.5 p-0.5 rounded-full border border-edge bg-white shadow-inner"
      role="radiogroup"
      aria-label="Modificatore di voce"
      data-testid={testId}
    >
      {VOICE_PRESETS.map((preset) => {
        const active = value === preset.key;
        return (
          <button
            key={preset.key}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(preset.key)}
            title={preset.hint}
            className={`h-8 px-3 rounded-full text-xs font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-ink text-white shadow" : "text-ink hover:bg-cream"}`}
            data-testid={testId + "-" + preset.key}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}
